import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import ModalShell, { inputClass, selectClass, textareaClass, labelClass, requiredMark, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error occurred';
  const { status, data } = error.response;
  if (data?.detail) {
    if (Array.isArray(data.detail)) {
      const first = data.detail[0];
      if (typeof first === 'object') return `${first.loc?.[first.loc.length - 1] || 'Field'}: ${first.msg || 'Invalid value'}`;
      return String(first);
    }
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }
  return `Request failed with status ${status}`;
};

const getDateString = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const QuotationForm = ({ quotation, onClose, onSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const today = new Date();
  const isEdit = !!quotation;

  const [formData, setFormData] = useState(() => {
    if (quotation) {
      return {
        customer_id: quotation.customer_id || '',
        issue_date: quotation.issue_date ? quotation.issue_date.slice(0, 10) : getDateString(today),
        expiry_date: quotation.expiry_date ? quotation.expiry_date.slice(0, 10) : '',
        notes: quotation.notes || '',
        gst_rate: quotation.gst_rate != null ? String(quotation.gst_rate) : '',
      };
    }
    return { customer_id: '', issue_date: getDateString(today), expiry_date: '', notes: '', gst_rate: '' };
  });

  const [items, setItems] = useState(() => {
    if (quotation && quotation.items && quotation.items.length) {
      return quotation.items.map((it) => ({
        product_id: it.product_id,
        unit_id: it.unit_id || '',
        quantity: String(it.quantity),
        unit_price: it.unit_price,
        discount_percent: it.discount_percent || 0,
      }));
    }
    return [{ product_id: '', unit_id: '', quantity: '1.00', unit_price: 0, discount_percent: 0 }];
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          api.get('/customers?is_active=true'),
          api.get('/products?limit=1000'),
        ]);
        setCustomers(cRes.data.items ?? cRes.data);
        setProducts((pRes.data.items ?? pRes.data.products ?? []).filter((p) => p.is_active));
      } catch (err) { console.error('Failed to load data', err); }
    })();
  }, []);

  const handleProductChange = (index, productId) => {
    const newItems = [...items];
    newItems[index].product_id = productId;
    newItems[index].unit_id = '';
    const product = products.find((p) => p.id === productId);
    if (product) {
      newItems[index].unit_price = parseFloat(product.selling_price) || 0;
      const defaultUnit = (product.units || []).find((u) => u.is_default);
      if (defaultUnit) {
        newItems[index].unit_id = defaultUnit.id;
        if (parseFloat(defaultUnit.selling_price) > 0) newItems[index].unit_price = parseFloat(defaultUnit.selling_price);
      }
    }
    setItems(newItems);
  };

  const handleUnitChange = (index, unitId) => {
    const newItems = [...items];
    newItems[index].unit_id = unitId;
    if (unitId) {
      const product = products.find((p) => p.id === newItems[index].product_id);
      const unit = (product?.units || []).find((u) => u.id === unitId);
      if (unit && parseFloat(unit.selling_price) > 0) newItems[index].unit_price = parseFloat(unit.selling_price);
    }
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => setItems([...items, { product_id: '', unit_id: '', quantity: '1.00', unit_price: 0, discount_percent: 0 }]);
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const lineTotal = (it) => {
    const q = parseFloat(it.quantity) || 0;
    const p = parseFloat(it.unit_price) || 0;
    const d = parseFloat(it.discount_percent) || 0;
    return q * p * (1 - d / 100);
  };
  const total = items.reduce((s, it) => s + lineTotal(it), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.customer_id) { setError('Please select a customer'); return; }
    if (items.length === 0 || !items[0].product_id) { setError('Please add at least one item'); return; }
    for (let i = 0; i < items.length; i++) {
      if (!items[i].product_id) { setError(`Item ${i + 1}: Please select a product`); return; }
      if (!items[i].quantity || parseFloat(items[i].quantity) <= 0) { setError(`Item ${i + 1}: Quantity must be greater than 0`); return; }
      if (parseFloat(items[i].unit_price) < 0) { setError(`Item ${i + 1}: Unit price cannot be negative`); return; }
    }
    setLoading(true);
    try {
      const payload = {
        customer_id: formData.customer_id,
        issue_date: formData.issue_date || getDateString(new Date()),
        expiry_date: formData.expiry_date || null,
        notes: formData.notes || null,
        gst_rate: formData.gst_rate !== '' ? parseFloat(formData.gst_rate) : null,
        items: items.map((it) => ({
          product_id: it.product_id,
          unit_id: it.unit_id || null,
          quantity: it.quantity,
          unit_price: parseFloat(it.unit_price),
          discount_percent: parseFloat(it.discount_percent) || 0,
        })),
      };
      if (isEdit) {
        await api.put(`/quotations/${quotation.id}`, payload);
      } else {
        await api.post('/quotations', payload);
      }
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
      console.error('Quotation save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const itemInputClass = 'w-full bg-white border border-[#d1d5db] rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] outline-none';

  return (
    <ModalShell
      title={isEdit ? 'Edit Quotation' : 'Create Quotation'}
      onClose={onClose}
      maxWidth="max-w-5xl"
      dataTestId="quotation-form-modal"
      footer={
        <div className="flex justify-between items-center">
          <div className="text-sm">
            <span className="text-[#6b7280]">Subtotal (excl. GST): </span>
            <span className="text-lg font-bold text-[#4A7AE0]" data-testid="quotation-total">Rs {total.toFixed(2)}</span>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors" data-testid="cancel-button">Cancel</button>
            <button type="submit" form="quotation-form" disabled={loading} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50" data-testid="save-quotation-button">{loading ? 'Saving...' : isEdit ? 'Update Quotation' : 'Create Quotation'}</button>
          </div>
        </div>
      }
    >
      <form id="quotation-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="form-error">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={labelClass}>Customer {requiredMark}</label>
            <select className={selectClass} value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} required data-testid="customer-select">
              <option value="">Select Customer</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Issue Date</label>
            <input type="date" className={inputClass} value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} data-testid="issue-date-input" />
          </div>
          <div>
            <label className={labelClass}>Expiry Date</label>
            <input type="date" className={inputClass} value={formData.expiry_date} onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })} data-testid="expiry-date-input" />
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-semibold text-[#1a1a2e]">Items</h3>
            <button type="button" onClick={addItem} className="text-[#4A7AE0] hover:underline text-sm font-medium flex items-center gap-1" data-testid="add-item-button">
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
          <div className="space-y-2">
            {items.map((item, index) => {
              const selectedProduct = products.find((p) => p.id === item.product_id);
              const productUnits = selectedProduct?.units || [];
              const baseUnitLabel = selectedProduct?.base_unit || selectedProduct?.unit || '';
              return (
                <div key={index} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <select className={itemInputClass} value={item.product_id} onChange={(e) => handleProductChange(index, e.target.value)} required data-testid={`product-select-${index}`}>
                      <option value="">Select Product</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <select className={`${itemInputClass} disabled:bg-[#f3f4f6]`} value={item.unit_id} onChange={(e) => handleUnitChange(index, e.target.value)} disabled={!item.product_id} data-testid={`unit-select-${index}`}>
                      <option value="">{baseUnitLabel || 'Base unit'}</option>
                      {productUnits.map((u) => <option key={u.id} value={u.id}>{u.unit_name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" min="0.01" placeholder="Qty" className={itemInputClass} value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} required data-testid={`quantity-input-${index}`} />
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" min="0" placeholder="Price" className={itemInputClass} value={item.unit_price} onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)} required data-testid={`price-input-${index}`} />
                  </div>
                  <div className="col-span-1">
                    <input type="number" step="0.01" min="0" max="100" placeholder="Disc%" className={itemInputClass} value={item.discount_percent} onChange={(e) => handleItemChange(index, 'discount_percent', e.target.value)} data-testid={`discount-input-${index}`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(index)} className="text-[#EF4444] hover:bg-[#FEE2E2] p-1.5 rounded-lg" data-testid={`remove-item-${index}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-end gap-3">
            <label className="text-sm text-[#6b7280]">GST %</label>
            <input type="number" step="0.01" min="0" max="100" placeholder="e.g. 17" className={`${inputClass} w-24 text-right`} value={formData.gst_rate} onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })} data-testid="gst-rate-input" />
          </div>
        </div>

        <div className="mb-4 p-3 bg-white rounded-lg border border-[#d1d5db]">
          <div className="flex justify-between text-sm text-[#6b7280]">
            <span>Subtotal (excl. GST)</span>
            <span data-testid="quotation-total">Rs {total.toFixed(2)}</span>
          </div>
          {formData.gst_rate !== '' && parseFloat(formData.gst_rate) > 0 && (
            <div className="flex justify-between text-sm text-[#6b7280] mt-1">
              <span>GST ({formData.gst_rate}%)</span>
              <span>Rs {(total * parseFloat(formData.gst_rate) / 100).toFixed(2)}</span>
            </div>
          )}
        </div>

        <div>
          <label className={labelClass}>Notes</label>
          <textarea className={textareaClass} rows="2" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="notes-input" placeholder="Terms, validity, delivery..." />
        </div>
      </form>
    </ModalShell>
  );
};

export default QuotationForm;
